import { Component, OnInit, Signal, computed } from '@angular/core';
import {
  CoverageComponentTypes,
  CoverageComponentTypeValues,
  CoverageTypes,
  ErrorCode,
  ICar,
  IConnectorChargingBenefitsDTO,
  IConnectorSponsorshipBenefitDTO,
  ICoverageRuleSummary,
  IEnergyWalletBalanceDTO,
  OCPITariffDimension,
  PlatformType,
  TariffUtil,
} from 'common_library';
import { CoreService } from 'src/app/services/core.service';
import { ToastService } from 'src/app/services/utils/toast.service';
import { ModalController } from '@ionic/angular';
import { _ } from 'src/app/consts';
import { TranslateConfigService } from 'src/app/services/translate.service';
import { TranslateService } from '@ngx-translate/core';
import { MainStateService } from 'src/app/services/state/app-main-state.service';
import { CmdButton } from 'src/app/types/buttons/button-state.interface';
import { BehaviorSubject } from 'rxjs';
import { EnergyWalletService } from 'src/app/services/entities/energy-wallet.service';
import { CarPage } from 'src/app/pages/car/car.page';
import { HostedHoldPhase, HostedHoldService, IHostedHoldVerification } from 'src/app/services/entities/hosted-hold.service';

/** Con quale strumento si paga QUESTO avvio. Il default resta la carta registrata. */
export type StartPaymentMethod = 'CARD' | 'HOSTED';

@Component({
    selector: 'yf-confirm-station-ops',
    templateUrl: './confirm-station-ops.component.html',
    styleUrls: ['./confirm-station-ops.component.scss'],
    standalone: false
})
export class ConfirmStationOpsComponent implements OnInit {

  currentStep$ = new BehaviorSubject<number>(0);
  energyWallet$ = new BehaviorSubject<IEnergyWalletBalanceDTO>(null);
  chargingBenefits$ = new BehaviorSubject<IConnectorChargingBenefitsDTO>(null);
  chargingBenefitsLoading$ = new BehaviorSubject<boolean>(false);
  paymentMethod$ = new BehaviorSubject<StartPaymentMethod>('CARD');
  /** Attesa del pagamento hosted, a stati reali: mai uno spinner senza nome. */
  hostedPhase$ = new BehaviorSubject<HostedHoldPhase>('idle');

  $tariffDeposit: Signal<number> = computed(() => {
    return this.mainStateService.$selectedConnector()?.tariff?.deposit;
  });

  $tariffText: Signal<string> = computed(() => {
    let tariff = this.mainStateService.$selectedConnector()?.tariff;
    try {
      const lang = this.translateConfigService.getCurrentLang();

      const descriptions = TariffUtil.getTariffAltTextForConnectorPower(tariff, lang, TariffUtil.getConnectorPower(this.mainStateService.$selectedConnector()));
      const reservationDescription = descriptions.find(c => c.dimension === OCPITariffDimension.RESERVATION);
      const energyDescription = descriptions.find(c => c.dimension === OCPITariffDimension.ENERGY);
      const parkingDescription = descriptions.find(c => c.dimension === OCPITariffDimension.PARKING_TIME);
      let textDescrption = `${reservationDescription.description} ${energyDescription.description} ${parkingDescription.description}`.trim();
      return textDescrption;
    } catch (e) {
      return tariff?.tariffAltText[0]?.text;
    }
  });

  constructor(
    public CS: CoreService,
    private toast: ToastService,
    private modalController: ModalController,
    public mainStateService: MainStateService,
    private translate: TranslateService,
    private translateConfigService: TranslateConfigService,
    private energyWalletService: EnergyWalletService,
    private hostedHold: HostedHoldService,
  ) { }

  /** Fuori audience il selettore non esiste proprio (decisione già presa dal backend). */
  hostedAvailable(): boolean {
    return this.hostedHold.isAvailable();
  }

  /**
   * Il bottone resta uno solo, ma promette il wallet che su questa piattaforma esiste
   * davvero: Apple Pay su iOS (app e Safari), Google Pay su Android e su web.
   * Il pagamento è comunque sulla pagina del PSP, dove c'è anche la carta.
   */
  walletBrand(): 'APPLE_PAY' | 'GOOGLE_PAY' {
    const platform = this.mainStateService.getPlatformType();
    const isIos = platform === PlatformType.iOSNative || platform === PlatformType.iOSMobileWeb;
    return isIos ? 'APPLE_PAY' : 'GOOGLE_PAY';
  }

  walletIcon(): string {
    return this.walletBrand() === 'APPLE_PAY' ? 'logo-apple' : 'logo-google';
  }

  walletKey(part: 'TITLE' | 'SUBTITLE'): string {
    return `APP.INSTALLATION.HOSTED_PAY.${this.walletBrand()}_${part}`;
  }

  selectPaymentMethod(method: StartPaymentMethod): void {
    if (this.hostedPhase$.value !== 'idle') return;
    this.paymentMethod$.next(method);
  }

  hostedPhaseLabel(): string {
    return this.translate.instant(`APP.INSTALLATION.HOSTED_PAY.PHASE_${this.hostedPhase$.value.toUpperCase()}`);
  }

  async ngOnInit(): Promise<void> {
    this.mainStateService.setSelectedUserCarForSession(this.mainStateService.$selectedUserCar());
    const connectorId = this.mainStateService.$selectedConnector()?.id;
    const requests = [
      this.loadEnergyWallet(),
      this.loadChargingBenefits(connectorId),
    ];
    await Promise.all(requests);
  }

  private async loadEnergyWallet(): Promise<void> {
    try {
      this.energyWallet$.next(await this.energyWalletService.getMine());
    } catch (error) {
      this.energyWallet$.next(null);
    }
  }

  private async loadChargingBenefits(connectorId?: number): Promise<void> {
    this.chargingBenefitsLoading$.next(!!connectorId);
    if (connectorId) {
      try {
        this.chargingBenefits$.next(await this.energyWalletService.getConnectorChargingBenefits(connectorId));
      } catch (error) {
        this.chargingBenefits$.next(null);
      } finally {
        this.chargingBenefitsLoading$.next(false);
      }
    } else {
      this.chargingBenefits$.next(null);
      this.chargingBenefitsLoading$.next(false);
    }
  }

  formatKwh(valueWh?: number): string {
    return `${(Number(valueWh || 0) / 1000).toFixed(2)} kWh`;
  }

  getVisibleSponsorships(benefits?: IConnectorChargingBenefitsDTO): IConnectorSponsorshipBenefitDTO[] {
    return (benefits?.sponsorships || [])
      .filter((sponsorship) => sponsorship?.userInDomain)
      .sort((a, b) => Number(b.primary) - Number(a.primary));
  }

  sponsorshipStatusLabel(sponsorship: IConnectorSponsorshipBenefitDTO): string {
    return this.translate.instant(
      sponsorship?.applied
        ? 'APP.INSTALLATION.BENEFITS.SPONSORSHIP_ACTIVE_NOW'
        : 'APP.INSTALLATION.BENEFITS.SPONSORSHIP_NOT_ACTIVE_NOW',
    );
  }

  hasCoverageRules(sponsorship?: IConnectorSponsorshipBenefitDTO | null): boolean {
    return !!sponsorship?.coverageRules?.length;
  }

  sponsorshipCoverageText(rule: ICoverageRuleSummary): string {
    const component = this.sponsorshipComponentLabel(rule?.componentType);
    const type = String(rule?.coverageType || '').toUpperCase();
    if (type === CoverageTypes.FULL) {
      return this.translate.instant('APP.INSTALLATION.BENEFITS.COVERAGE_FULL', { component });
    }
    if (type === CoverageTypes.PERCENT) {
      return this.translate.instant('APP.INSTALLATION.BENEFITS.COVERAGE_PERCENT', {
        component,
        value: Number(rule?.value || 0),
      });
    }
    if (type === CoverageTypes.FIXED_DISCOUNT) {
      return this.translate.instant('APP.INSTALLATION.BENEFITS.COVERAGE_FIXED_DISCOUNT', {
        component,
        value: Number(rule?.value || 0),
        unit: rule?.unit || '',
      });
    }
    if (type === CoverageTypes.NONE) {
      return this.translate.instant('APP.INSTALLATION.BENEFITS.COVERAGE_NONE', { component });
    }
    return this.translate.instant('APP.INSTALLATION.BENEFITS.COVERAGE_CONFIGURED', { component });
  }

  private sponsorshipComponentLabel(componentType?: CoverageComponentTypeValues | string | null): string {
    switch (componentType) {
      case CoverageComponentTypes.ENERGY:
        return this.translate.instant('APP.INSTALLATION.BENEFITS.COMPONENT_ENERGY');
      case CoverageComponentTypes.TIME:
        return this.translate.instant('APP.INSTALLATION.BENEFITS.COMPONENT_TIME');
      case CoverageComponentTypes.PARKING_TIME:
        return this.translate.instant('APP.INSTALLATION.BENEFITS.COMPONENT_PARKING');
      case CoverageComponentTypes.RESERVATION:
        return this.translate.instant('APP.INSTALLATION.BENEFITS.COMPONENT_RESERVATION');
      case CoverageComponentTypes.FLAT:
        return this.translate.instant('APP.INSTALLATION.BENEFITS.COMPONENT_FLAT');
      default:
        return componentType || this.translate.instant('APP.INSTALLATION.BENEFITS.COMPONENT_GENERIC');
    }
  }

  async confirmOperation() {
    if (this.mainStateService.$connectorCommandInProgress()) return;
    if (this.hostedPhase$.value !== 'idle') return;

    // Pagamento sulla pagina del PSP: si fa PRIMA di toccare lo stato del pulsante,
    // così un pagamento non riuscito lascia tutto com'era. Nessun ripiego silenzioso
    // sulla carta: l'utente crede di aver pagato lì, avviare con un altro strumento
    // gli bloccherebbe i fondi due volte.
    let hostedHoldPaymentId: string | undefined;
    if (this.hostedAvailable() && this.paymentMethod$.value === 'HOSTED') {
      hostedHoldPaymentId = await this.authorizeHostedHold();
      if (!hostedHoldPaymentId) return;
    }

    const currentConnector = this.mainStateService.$selectedConnector();
    var expiringDate = new Date();
    expiringDate.setSeconds(expiringDate.getSeconds() + _.OCPI_SESSION_TIMEOUT);
    this.mainStateService.updateButtonState(currentConnector.id, { exipresAt: expiringDate, command: CmdButton.StartCharge });
    await this.modalController.dismiss();

    const rv = await this.CS.startCharge(currentConnector.id, this.mainStateService.$selectedConnectorMineSession()?.id, this.mainStateService.getLastClientPosition(), hostedHoldPaymentId);

    // Hold speso: se l'avvio è fallito NON si consuma, così un secondo tentativo
    // riusa lo stesso hold invece di bloccare i fondi un'altra volta.
    if (hostedHoldPaymentId && rv?.errorCode === ErrorCode.NoError) this.hostedHold.consume();

    // Comando non partito: il pulsante torna subito disponibile, altrimenti l'utente resta davanti a
    // un "Avvia" grigio senza sapere che deve chiudere e riaprire la scheda per sbloccarlo.
    if (!rv || rv.errorCode !== ErrorCode.NoError) {
      this.mainStateService.clearButtonState(currentConnector.id);
    }

    if (!!rv && rv?.errorCode !== ErrorCode.NoError) {
      await this.CS.manageErrorCodes(rv.errorCode, rv.message);
    } else if (!rv) {
      await this.toast.presentBasicToast({ message: this.translate.instant("TOAST_MESSAGES.COMMAND_NOT_SENT"), color: 'danger', icon: 'warning' });
    } else {
      const message = currentConnector.innerState === 'Preparing' ? this.translate.instant("APP.INSTALLATION.START_CHARGING") : this.translate.instant("APP.INSTALLATION.CONNECT_PLUG_AND_WAIT_STARTING")
      await this.toast.presentBasicToast({ message, color: 'tertiary', icon: 'flash-outline' });
    }

    // `rv` è undefined su qualunque errore di rete (requestCommand lo intercetta): senza `?.` qui
    // partiva un TypeError non gestito ad ogni avvio fallito.
    if (!!rv?.session && rv?.errorCode === ErrorCode.NoError) {
      this.mainStateService.updateSessionsWithCurrent(rv.session);
    }
  }

  /**
   * Hold sulla pagina del PSP: `null` = sessione da NON avviare (messaggio già
   * mostrato). Ogni esito ha il suo messaggio — rifiutato, non completato e
   * "non ci siamo riusciti a chiedere" sono cose diverse per chi legge.
   */
  private async authorizeHostedHold(): Promise<string | null> {
    let outcome: IHostedHoldVerification;
    try {
      outcome = await this.hostedHold.authorize((phase) => this.hostedPhase$.next(phase));
    } catch (err) {
      // browser di sistema non disponibile o eccezione imprevista: mai lasciare
      // il pulsante in attesa, e mai avviare la ricarica al buio
      console.log('🐱️ : hosted hold non riuscito', err);
      outcome = { status: 'ERROR', paymentId: null };
    } finally {
      this.hostedPhase$.next('idle');
    }
    if (outcome.status === 'AUTHORIZED') return outcome.paymentId;
    // Web: l'app sta uscendo verso la pagina di pagamento, l'esito arriva al rientro.
    if (outcome.status === 'REDIRECTED') return null;
    await this.toast.presentBasicToast({
      header: this.translate.instant("APP.INSTALLATION.HOSTED_PAY.FAILED_HEADER"),
      message: this.translate.instant(this.hostedHold.messageKey(outcome.status)),
      color: 'danger',
      icon: 'warning-outline',
    });
    return null;
  }

  selectNewCar(car: ICar) {
    this.mainStateService.setSelectedUserCarForSession(car);
  }

  async addVehicle(): Promise<void> {
    const modal = await this.modalController.create({ component: CarPage, cssClass: 'yf-modal' });
    await modal.present();
  }

  close() {
    this.modalController.dismiss();
  }

  showNextStep() {
    this.currentStep$.next(this.currentStep$.value + 1)
  }

  showPrevStep() {
    this.currentStep$.next(this.currentStep$.value > 0 ? this.currentStep$.value - 1 : 0)
  }
}
